import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import SegmentGauge from './SegmentGauge';

/******************************************************************
 * 출퇴근 팝업용 근무 진행 게이지
 * - 출근 시각 ~ 지금(퇴근했으면 퇴근 시각)까지 흐른 시간을 계속 갱신
 * - 1초마다 다시 그려지는 건 이 컴포넌트뿐 → AttendanceHome 전체는 그대로
 ******************************************************************/

const useElapsedSeconds = (startTime, endTime) => {
	// 현재 시각
	const [nowMs, setNowMs] = useState(() => Date.now());

	// 출근은 했고 퇴근은 안 했을 때만 시간이 흘러야 함
	const isRunning = Boolean(startTime) && !endTime;

	useEffect(() => {
		if (!isRunning) return undefined;

		// 타이머 시작 순간에도 한 번 맞춰 줌 (모달을 오래 열어둔 뒤 출근한 경우 대비)
		setNowMs(Date.now());
		const timerId = setInterval(() => setNowMs(Date.now()), 1000);

		// 퇴근했거나 화면이 닫히면 타이머 정리 (안 하면 계속 돌아감)
		return () => clearInterval(timerId);
	}, [isRunning]);

	if (!startTime) return 0;

	const endMs = endTime ? moment(endTime).valueOf() : nowMs;
	const diffSeconds = Math.floor((endMs - moment(startTime).valueOf()) / 1000);

	// PC 시계가 서버보다 느리면 음수가 나올 수 있어서 0 아래로 안 내려가게
	return Math.max(0, diffSeconds);
};

// 'HH:mm' 두 개 → 소정 근무 총 초 (자정 넘는 근무 대비)
const getScheduleSeconds = (scheduleStart, scheduleEnd) => {
	if (!scheduleStart || !scheduleEnd) return 0;
	const diff = moment(scheduleEnd, 'HH:mm').diff(moment(scheduleStart, 'HH:mm'), 'seconds');
	return diff > 0 ? diff : diff + 24 * 60 * 60;
};

const WorkGauge = ({ startTime = '', endTime = '', scheduleStart = '', scheduleEnd = '' }) => {
	const { t } = useTranslation();

	// 출근부터 현재까지 흐른 초 (1초마다 바뀜)
	const workedSeconds = useElapsedSeconds(startTime, endTime);

	const hours = Math.floor(workedSeconds / 3600); // 3600초 = 1시간
	const minutes = Math.floor((workedSeconds % 3600) / 60); // 시간 빼고 남은 초 → 분

	const scheduleSeconds = getScheduleSeconds(scheduleStart, scheduleEnd);
	const percent = scheduleSeconds > 0 ? Math.round((workedSeconds / scheduleSeconds) * 100) : 0;

	return (
		<SegmentGauge
			value={percent}
			ariaLabel={t('근무 진행률')}
			time={t('경과시간', { hours, minutes })}
		/>
	);
};

WorkGauge.propTypes = {
	startTime: PropTypes.string,
	endTime: PropTypes.string,
	scheduleStart: PropTypes.string,
	scheduleEnd: PropTypes.string,
};

export default WorkGauge;